import type { APMAsset, ICDFView } from '@cognite/apm-client';
import { FDMClient } from '@cognite/fdm-client';
import type { Filters, Sort } from '@cognite/fdm-client';
import type {
  Asset,
  AssetAggregatedProperty,
  AssetFilterProps,
  AssetListScope,
  AssetRetrieveParams,
  CogniteClient,
  CursorResponse,
  IdEither,
  NodeOrEdge,
  ViewReference,
} from '@cognite/sdk';

import type {
  HierarchyQueryOptions,
  IAssetService,
} from './asset-service-interface';
import type { AssetListSortFilters } from './types';

export const LIST_ASSETS_WITH_SORT_FILTERS_AND_AGGREGATED_PROPERTIES_LIMIT = 1000;

type AssetsViewReference = Pick<ViewReference, 'version'>;

type CogniteClientInstancesDependencies = Pick<
  CogniteClient['instances'],
  'search'
>;
type CogniteClientViewsDependencies = Pick<CogniteClient['views'], 'retrieve'>;

export type APMClientDependencies = {
  assets: Pick<ICDFView<APMAsset>, 'list'> & {
    viewReference: AssetsViewReference;
  };
};

export type CogniteClientDependencies = Pick<
  CogniteClient,
  'post' | 'getBaseUrl' | 'project'
> & {
  instances: CogniteClientInstancesDependencies;
  views: CogniteClientViewsDependencies;
  assets: Pick<CogniteClient['assets'], 'list' | 'retrieve' | 'search'>;
};

export class AssetService extends FDMClient implements IAssetService {
  client: CogniteClientDependencies;
  apmClient: APMClientDependencies;

  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies
  ) {
    super(client);
    this.client = client;
    this.apmClient = apmClient;
  }

  /**
   * Fetches APM assets by externalId across all spaces
   * @param assetExternalIds
   * @param akerbpCustomFilter
   * @returns
   */
  async getAPMAssetByExternalIds(
    assetExternalIds: string[],
    akerbpCustomFilter?: boolean
  ) {
    if (assetExternalIds.length === 0) return [];
    const uniqueAssetExternalIds = [...new Set(assetExternalIds)];
    const { items: assets } = await this.apmClient.assets.list({
      filters: {
        in: {
          property: 'externalId',
          in: uniqueAssetExternalIds,
        },
      },
      spaces: [], // Query across all spaces
    });

    if (assets && assets.length > 0 && akerbpCustomFilter) {
      // TODO(FUS-000): When AkerBP migrates to IDM, check if asset is voided (WORKFLOW STATUS == 'void' || 'removed')
    }
    return assets;
  }

  async getAssetByExternalId(
    assetExternalId: string,
    akerbpCustomFilter?: boolean
  ) {
    const [assetData] = await this.client.assets.retrieve([
      { externalId: assetExternalId },
    ]);
    if (akerbpCustomFilter) {
      if (
        assetData.metadata?.['WORKFLOW STATUS']?.toLowerCase() === 'void' ||
        assetData.metadata?.['WORKFLOW STATUS']?.toLowerCase() === 'removed'
      ) {
        throw new Error('Asset is voided');
      }
    }
    return assetData;
  }

  async getAssetByExternalIds(assetExternalId: string[]) {
    const assetsData = await this.client.assets.retrieve(
      assetExternalId.map((externalId) => ({ externalId })),
      {
        ignoreUnknownIds: true,
      }
    );
    return assetsData;
  }

  async getAssetById(assetId: number) {
    const [assetData] = await this.client.assets.retrieve([{ id: assetId }]);
    return assetData;
  }

  async getAssets(filter?: AssetFilterProps, akerbpCustomFilter?: boolean) {
    const { items: assets } = await this.client.assets.list({
      filter,
    });
    return assets.filter((asset) => {
      if (akerbpCustomFilter) {
        return (
          asset.metadata?.['WORKFLOW STATUS']?.toLowerCase() !== 'void' &&
          asset.metadata?.['WORKFLOW STATUS']?.toLowerCase() !== 'removed'
        );
      }
      return asset;
    });
  }

  async getAssetByName(
    name: string,
    cdfClassicFilters: AssetFilterProps | undefined
  ) {
    const {
      items: [asset],
    } = await this.client.assets.list({
      filter: { name, ...cdfClassicFilters },
      limit: 1,
    });
    return asset;
  }

  /**
   * TODO(INFIELD2-2548): Replace this method's filter with new upcoming solution (isRoot property), since this filter is not performant for large numbers of assets (e.g. > 100 000).
   * @returns Root assets across all spaces
   */
  async getRootAPMAssets() {
    const rootAssets = await this.apmClient.assets.list({
      filters: {
        nested: {
          scope: 'parent',
          filters: {
            isNull: {
              property: 'externalId',
              isNull: true,
            },
          },
        },
      },
      spaces: [], // Query across all spaces
    });
    return rootAssets.items;
  }

  async getRootAssets() {
    const rootAssets = await this.client.assets.list({
      filter: { root: true },
      limit: 1000,
    });
    return rootAssets.items;
  }

  async getAPMAsset(
    filters?: Filters,
    sort?: Sort,
    pageSize = 1000
  ): Promise<APMAsset | undefined> {
    const {
      items: [asset],
    } = await this.apmClient.assets.list({ filters, sort, pageSize });
    return asset;
  }

  async listAPMAssets(
    filters?: Filters,
    sort?: Sort,
    pageSize = 1000,
    spaces?: string[]
  ) {
    const { items: assets } = await this.apmClient.assets.list({
      filters,
      sort,
      pageSize,
      spaces,
    });
    return assets;
  }

  async searchAssets(
    query?: string,
    limit?: number,
    filter?: AssetFilterProps,
    akerbpCustomFilter?: boolean
  ): Promise<Asset[]> {
    const assets = await this.client.assets.search({
      search: {
        query,
      },
      limit,
      filter,
    });
    return assets.filter((asset) => {
      if (akerbpCustomFilter) {
        return (
          asset.metadata?.['WORKFLOW STATUS']?.toLowerCase() !== 'void' &&
          asset.metadata?.['WORKFLOW STATUS']?.toLowerCase() !== 'removed'
        );
      }
      return asset;
    });
  }

  async getAPMAssetByName(name: string, filter?: Filters) {
    const filters: Filters[] = [];

    if (filter) {
      filters.push({ ...filter });
    }
    const nameFilter = {
      equals: {
        property: 'title',
        eq: name,
      },
    };
    filters.push(nameFilter);

    const {
      items: [asset],
    } = await this.apmClient.assets.list({
      filters: {
        and: [...filters],
      },
    });

    return asset;
  }

  /**
   * Retrieves a list of assets based on a filter, with aggregated properties to build asset hierarchy.
   *
   * @param filter - The filter to apply to the asset list.
   * @param aggregatedProperties - A list of aggregated properties to include in the response.
   * @param akerbpCustomFilter - An optional boolean to apply a custom AkerBP filter that excludes assets with 'Void' or 'Removed' workflow statuses.
   * @param limit - The maximum number of assets to retrieve.
   * @returns A promise that resolves to an array of Assets.
   */
  async getAssetsWithAggregatedProps(
    filter: AssetFilterProps,
    aggregatedProperties: AssetAggregatedProperty[],
    akerbpCustomFilter?: boolean,
    limit?: number
  ): Promise<Asset[]> {
    if (aggregatedProperties.length < 1) {
      throw new Error('Aggregated properties is empty');
    }
    if (limit && (limit < 1 || limit > 1000)) {
      throw new Error('Limit must be between 1 and 1000');
    }

    try {
      const apiParams: AssetListScope = {
        filter,
        aggregatedProperties,
      };
      if (limit) {
        apiParams.limit = limit;
      }

      const { items: assets } = await this.client.assets.list(apiParams);

      if (akerbpCustomFilter) {
        return assets.filter(
          (asset) =>
            asset.metadata?.['WORKFLOW STATUS']?.toLowerCase() !== 'void' &&
            asset.metadata?.['WORKFLOW STATUS']?.toLowerCase() !== 'removed'
        );
      }

      return assets;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      throw new Error(
        `Failed to retrieve assets with aggregated properties: ${errorMessage}`
      );
    }
  }

  /**
   * Retrieves assets by their InternalIDs or ExternalIDs, with support for aggregated properties.
   *
   * @param assetIds - An array of asset's InternalIDs or ExternalIDs to retrieve.
   * @param aggregatedProperties - A list of aggregated properties to include in the response.
   * @returns A promise that resolves to an array of Assets.
   */
  async getAssetsByInternalExternalIDsWithAggregatedProps(
    assetIds: number[] | string[],
    aggregatedProperties: AssetAggregatedProperty[]
  ): Promise<Asset[]> {
    if (assetIds.length < 1) {
      throw new Error('Asset IDs array is empty');
    }
    if (aggregatedProperties.length < 1) {
      throw new Error('Aggregated properties is empty');
    }

    try {
      const apiParams: AssetRetrieveParams = {
        ignoreUnknownIds: true,
        aggregatedProperties,
      };

      const idObjects: IdEither[] = assetIds.map((id) =>
        typeof id === 'string' ? { externalId: id } : { id }
      );

      return await this.client.assets.retrieve(idObjects, apiParams);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      throw new Error(
        `Failed to retrieve assets by IDs with aggregated properties: ${errorMessage}`
      );
    }
  }

  /**
   * Lists assets with support for advanced filtering and aggregated properties.
   * Existing Search Assets doesn't support aggregated properties. So we are leveraging the List Assets endpoint with advanced filtering to achieve this.
   * In Javascript SDK, the type for Advanced Filter is not available, so we are hard coding it here.
   *
   * @param filters - A filter to apply to the list.
   * @param customSortFilters - An array of custom sort filters to apply to the results. Results are sorted by '_score_' by default, with custom sort filters appended.
   * @param aggregatedProperties - A list of aggregated properties to include in the response. Defaults to 'childCount' and 'path'.
   * @param limit - The maximum number of results to retrieve. Defaults to 500.
   * @returns A promise that resolves to an array of matching assets sorted by the sort filters, or an empty array if no assets are found.
   */
  async listAssetsWithSortFiltersAndAggregatedProperties(
    filters: AssetFilterProps,
    customSortFilters: AssetListSortFilters[] = [],
    aggregatedProperties: AssetAggregatedProperty[],
    limit?: number
  ): Promise<Asset[]> {
    const sortFilters = [
      {
        property: ['_score_'],
        order: 'desc',
      },
    ];
    try {
      if (customSortFilters.length > 0) {
        sortFilters.push(...customSortFilters);
      }

      const { data } = await this.client.post<CursorResponse<Asset[]>>(
        `/api/v1/projects/${this.client.project}/assets/list`,
        {
          headers: {
            'cdf-version': 'alpha',
          },
          data: {
            limit:
              limit && limit > 0
                ? limit
                : LIST_ASSETS_WITH_SORT_FILTERS_AND_AGGREGATED_PROPERTIES_LIMIT,
            filter: filters,
            sort: sortFilters,
            aggregatedProperties:
              aggregatedProperties?.length > 0
                ? aggregatedProperties
                : ['childCount', 'path'],
          },
        }
      );
      return data?.items?.length > 0 ? data.items : [];
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to list assets with sorting: ${errorMessage}`);
    }
  }

  async getRootInstances(
    _options?: HierarchyQueryOptions
  ): Promise<NodeOrEdge[]> {
    throw new Error('Not implemented');
  }

  async getChildInstances(
    _parent: { externalId: string; space: string },
    _options?: HierarchyQueryOptions
  ): Promise<NodeOrEdge[]> {
    throw new Error('Not implemented');
  }

  async getChildrenCounts(
    _parents: { externalId: string; space: string }[],
    _options?: HierarchyQueryOptions
  ): Promise<Record<string, number>> {
    throw new Error('Not implemented');
  }

  async getInstancesByIds(
    _ids: { externalId: string; space: string }[],
    _options?: Pick<HierarchyQueryOptions, 'viewReference'>
  ): Promise<NodeOrEdge[]> {
    throw new Error('Not implemented');
  }
}

export const createAssetService = (
  ...args: ConstructorParameters<typeof AssetService>
): IAssetService => new AssetService(...args);
